import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("ceo_token"));
  const [role, setRole] = useState(() => localStorage.getItem("ceo_role") || "admin");
  const [permissions, setPermissions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ceo_permissions") || "{}"); }
    catch { return {}; }
  });
  const [accessProfile, setAccessProfile] = useState(() => localStorage.getItem("ceo_access_profile") || "");
  const [userInfo, setUserInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ceo_user") || "null"); }
    catch { return null; }
  });
  const [setupRequired, setSetupRequired] = useState(null);
  const [securityQuestion, setSecurityQuestion] = useState(null);
  const [questionsList, setQuestionsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/status");
      setSetupRequired(r.data.setup_required);
      setSecurityQuestion(r.data.security_question);
      setQuestionsList(r.data.security_questions_list || []);
    } catch (e) {
      console.error("auth status error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Refresh identity + server-side access profile on token change/page reload.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [meResponse, accessResponse] = await Promise.all([
          api.get("/auth/me"),
          api.get("/admin/collaborators/access-profile"),
        ]);
        if (cancelled) return;
        const me = meResponse.data || {};
        const access = accessResponse.data || {};
        const nextRole = me.role || "admin";
        const u = { id: me.id, nome: me.nome, email: me.email };
        const perms = { ...(me.permissions || {}), ...(access.permissions || {}) };
        const profile = access.access_profile || (nextRole === "admin" ? "admin" : "equipe");
        setRole(nextRole);
        setUserInfo(u);
        setPermissions(perms);
        setAccessProfile(profile);
        localStorage.setItem("ceo_role", nextRole);
        localStorage.setItem("ceo_user", JSON.stringify(u));
        localStorage.setItem("ceo_permissions", JSON.stringify(perms));
        localStorage.setItem("ceo_access_profile", profile);
      } catch (e) {
        console.error("[auth] profile refresh failed:", e?.response?.status || e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const login = async (payload) => {
    const body = typeof payload === "string" ? { password: payload } : payload;
    const r = await api.post("/auth/login", body);
    if (r.data?.totp_required || !r.data?.token) return r.data;
    localStorage.setItem("ceo_token", r.data.token);
    localStorage.setItem("ceo_role", r.data.role || "admin");
    const u = { nome: r.data.nome || null, email: r.data.email || null };
    localStorage.setItem("ceo_user", JSON.stringify(u));
    setToken(r.data.token);
    setRole(r.data.role || "admin");
    setUserInfo(u);
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {
      console.error("[auth] server logout failed:", e?.response?.status || e?.message);
    }
    localStorage.removeItem("ceo_token");
    localStorage.removeItem("ceo_role");
    localStorage.removeItem("ceo_user");
    localStorage.removeItem("ceo_permissions");
    localStorage.removeItem("ceo_access_profile");
    setToken(null);
    setRole("admin");
    setUserInfo(null);
    setPermissions({});
    setAccessProfile("");
  };

  const setup = async (payload) => {
    const r = await api.post("/auth/setup", payload);
    await loadStatus();
    return r.data;
  };

  const recover = async (payload) => (await api.post("/auth/recover", payload)).data;

  const masterUnlocked = !!token;
  const isAdmin = role === "admin";
  const isCollaborator = role === "colaborador";
  const readOnly = isCollaborator && !!permissions?.read_only;

  const value = useMemo(() => ({
    token, role, userInfo, permissions, accessProfile, readOnly, isAdmin, isCollaborator,
    can: (flag) => isAdmin || !!permissions?.[flag],
    setupRequired, securityQuestion, questionsList,
    masterUnlocked, loading,
    login, logout, setup, recover,
    reload: loadStatus,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [token, role, userInfo, permissions, accessProfile, readOnly, isAdmin, isCollaborator,
        setupRequired, securityQuestion, questionsList, masterUnlocked, loading, loadStatus]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
